/**
 * Ageon — Payment Ledger
 * Mirrors the Daewoo "Payment ledger" view: portfolio totals, monthly payment
 * columns, sticky ID + Client, save/reset view, Excel export.
 * Route: /admin/payment-ledger
 */
import { useEffect, useMemo, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
    Search, RefreshCw, Save, RotateCcw,
    FileSpreadsheet, Wallet, BarChart3,
    Activity, CheckCircle2, Clock, Filter,
    Download as DLIcon,
} from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../utils/api';

/* ──────────────────────────── helpers ────────────────────────────────── */
const inr = (v) =>
    new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(Number(v || 0));
const pct = (v, d = 1) => `${Number.isFinite(v) ? v.toFixed(d) : '0.0'}%`;
const csvEscape = (s) => `"${String(s ?? '').replace(/"/g, '""')}"`;
const fd = (d) => {
    if (!d) return '—';
    const p = new Date(d);
    return isNaN(p) ? '—' : p.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/* short display ID from mongo _id */
const short = (id) => (id ? String(id).slice(-6).toUpperCase() : '—');
const ageonId = (r) => `AG-${short(r._id)}-${new Date(r.createdAt).getFullYear()}`;

const getFranchiseLabel = (type) => {
    const m = {
        SignatureFico: 'Signature FICO',
        Signature: 'Signature FOFO',
        'Master Franchise': 'Master Franchise',
        'Daewoo District Partner Franchise': 'Daewoo District Partner Franchise',
    };
    return m[type] || type || '—';
};

/* ── all financial fields live inside r.data ──
 * F7: `deal` and `finalPayable` are distinct numbers — deal is the gross
 * sticker price the sales team negotiates, finalPayable is what the
 * client actually owes after GST/TDS. The receivedPercent must divide by
 * finalPayable (the real target) — dividing by dealAmount over-counts
 * because payments include the GST portion, producing >100% bars. */
function getPaymentMetrics(client) {
    if (client.status !== 'approved') {
        return {
            deal: 0, finalPayable: 0, gst: 0, tds: 0, tokenNet: 0, approvedTotal: 0,
            totalReceived: 0, receivedPercent: 0, rawPercent: 0, overpaid: false,
            balance: 0, pendingVerify: 0, inactive: true,
        };
    }
    const d = client.data || {};
    const deal         = Number(d.dealAmount || 0);
    const gst          = Number(d.gstAmount || 0);
    const tds          = Number(d.tdsAmount || 0);
    /* Prefer the snapshot if present; otherwise reconstruct from deal+gst-tds.
       Either way, finalPayable is what the percentage divides by. */
    const finalPayable = Number(d.finalPayableAmount) || Math.max(deal + gst - tds, deal) || 0;

    const approvedPayments = (client.secondPayments || []).filter(
        (p) => p.status === 'approved' && !p.isRefunded && !p.promotedToToken,
    );
    const approvedTotal = approvedPayments.reduce((s, p) => s + Number(p.amount || 0), 0);
    /* Trust the server's rollups (promotion- and refund-aware). effectiveTokenAmount
       reflects a token that was refunded and/or replaced by a promoted instalment.
       Fall back to the old client-side math only for pre-migration records. */
    const tokenNet      = client.effectiveTokenAmount != null
        ? Number(client.effectiveTokenAmount)
        : Math.max(0, Number(d.tokenReceivedAmount || 0) - Number(client.totalRefunded || 0));
    const totalReceived = client.totalReceived != null
        ? Number(client.totalReceived)
        : tokenNet + approvedTotal;
    const rawPercent    = finalPayable > 0 ? (totalReceived / finalPayable) * 100 : 0;
    /* Cap the display at 100 but expose the raw value too, so the UI can
       flag overpayment as a chip instead of a 116% bar. */
    const receivedPercent = Math.min(100, rawPercent);
    const overpaid        = rawPercent > 100.5;
    const balance       = Math.max(finalPayable - totalReceived, 0);
    const pendingVerify = (client.secondPayments || []).filter((p) => p.status === 'pending').length;
    return { deal, finalPayable, gst, tds, tokenNet, approvedTotal, totalReceived, receivedPercent, rawPercent, overpaid, balance, pendingVerify };
}

/* aggregate financials for the filtered list */
function aggregateFinancials(clients) {
    const r = { deal: 0, payable: 0, gst: 0, tds: 0, collected: 0, balanceDue: 0, token: 0, inst: 0, ref: 0, colRate: 0 };
    clients.forEach((c) => {
        const m = getPaymentMetrics(c);
        r.deal += m.deal;
        r.payable += m.finalPayable;
        r.gst += m.gst;
        r.tds += m.tds;
        r.collected += m.totalReceived;
        r.balanceDue += m.balance;
        r.token += m.tokenNet;
        r.inst += m.approvedTotal;
        r.ref += Number(c.totalRefunded || 0);
    });
    r.colRate = r.payable > 0 ? (r.collected / r.payable) * 100 : 0;
    return r;
}

/* group approved payments by calendar month — reads from r.data */
function groupByMonth(client) {
    const d = client.data || {};
    const months = Object.fromEntries(MONTHS.map((m) => [m, []]));
    const tokenDate = d.tokenDate ? new Date(d.tokenDate) : null;
    if (tokenDate && !isNaN(tokenDate) && Number(d.tokenReceivedAmount || 0) > 0) {
        months[MONTHS[tokenDate.getMonth()]].push({
            date: tokenDate, amount: Number(d.tokenReceivedAmount || 0), isToken: true,
        });
    }
    (client.secondPayments || [])
        .filter((p) => p.status === 'approved' && !p.isRefunded && !p.promotedToToken && p.paymentDate)
        .forEach((p) => {
            const dt = new Date(p.paymentDate);
            if (isNaN(dt)) return;
            months[MONTHS[dt.getMonth()]].push({ date: dt, amount: Number(p.amount || 0), isToken: false });
        });
    Object.values(months).forEach((arr) => arr.sort((a, b) => a.date - b.date));
    return months;
}

/* ──────────────────────────── sub-components ─────────────────────────── */
const AccentFinCard = ({ label, value, labelClass }) => (
    <div className="relative flex overflow-hidden rounded-xl border border-slate-200/80 bg-white
                    shadow-[0_2px_12px_rgba(15,23,42,0.06)] ring-1 ring-slate-100/60 min-h-[5.5rem]">
        <div className="flex flex-1 flex-col justify-center px-3 py-3 sm:px-4 min-w-0">
            <div className={`text-[9px] sm:text-[10px] font-bold uppercase tracking-[0.14em] leading-tight ${labelClass}`}>
                {label}
            </div>
            <div className="mt-1.5 font-heading text-lg sm:text-xl font-extrabold tabular-nums tracking-tight text-slate-900 truncate"
                title={value}>
                {value}
            </div>
        </div>
    </div>
);

const StatCard = ({ icon: Icon, label, value, sub }) => (
    <div className="relative overflow-hidden rounded-2xl border border-white/60 bg-white/80 p-4
                    shadow-[0_2px_8px_rgba(15,23,42,0.06)] backdrop-blur-sm ring-1 ring-slate-100/80">
        <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
                <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">{label}</div>
                <div className="font-heading text-2xl font-extrabold mt-1.5 tabular-nums tracking-tight text-slate-900">{value}</div>
                {sub && <div className="text-[11px] text-slate-500 mt-1 truncate">{sub}</div>}
            </div>
            <div className="h-11 w-11 rounded-xl bg-white/90 shadow-sm grid place-items-center shrink-0 ring-1 ring-slate-100">
                <Icon className="h-5 w-5 text-slate-600" />
            </div>
        </div>
    </div>
);

const PaymentTrackerCell = ({ client }) => {
    const { finalPayable, receivedPercent, pendingVerify } = getPaymentMetrics(client);
    if (finalPayable <= 0) return <span className="text-slate-300 text-xs">—</span>;
    return (
        <div className="flex flex-col gap-1 min-w-[8rem]">
            <div className="flex items-center gap-1.5">
                <div className="flex-1 h-1.5 rounded-full bg-slate-200 overflow-hidden">
                    <div
                        className="h-full rounded-full bg-gradient-to-r from-teal-500 to-emerald-500"
                        style={{ width: `${Math.min(100, receivedPercent)}%` }}
                    />
                </div>
                <span className="text-[10px] font-bold tabular-nums text-slate-700">{pct(receivedPercent)}</span>
            </div>
            {pendingVerify > 0 && (
                <span className="inline-flex items-center gap-1 text-[10px] text-amber-700 font-semibold">
                    <span className="w-1.5 h-1.5 rounded-full bg-amber-500 inline-block" />
                    {pendingVerify} pending
                </span>
            )}
        </div>
    );
};

/* ──────────────────────────── constants ──────────────────────────────── */
const STORAGE_KEY = 'ageon_payment_ledger_view_v1';

/* ════════════════════════════ COMPONENT ══════════════════════════════════ */
export default function AgeonPaymentLedger() {
    const navigate = useNavigate();

    const [clients, setClients] = useState([]);
    const [loading, setLoading] = useState(false);
    const [refreshing, setRefreshing] = useState(false);

    /* ── filters ── */
    const [search, setSearch] = useState('');
    const [franchiseLine, setFranchiseLine] = useState('');
    const [officeBranch, setOfficeBranch] = useState('');
    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');
    const [paymentTrackFilter, setPaymentTrackFilter] = useState('');
    const [ledgerPaymentYear, setLedgerPaymentYear] = useState('');
    const [ledgerPaymentMonth, setLedgerPaymentMonth] = useState('');
    const [showLedgerFilters, setShowLedgerFilters] = useState(false);

    /* ── load ── */
    const load = async (refresh = false) => {
        refresh ? setRefreshing(true) : setLoading(true);
        try {
            const { data } = await api.get('/forms/submissions');
            setClients(data.items || []);
        } catch {
            toast.error('Failed to load clients');
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    };

    /* ── restore saved view ── */
    useEffect(() => {
        try {
            const raw = localStorage.getItem(STORAGE_KEY);
            if (!raw) return;
            const v = JSON.parse(raw);
            if (typeof v.search === 'string') setSearch(v.search);
            if (typeof v.franchiseLine === 'string') setFranchiseLine(v.franchiseLine);
            if (typeof v.officeBranch === 'string') setOfficeBranch(v.officeBranch);
            if (typeof v.dateFrom === 'string') setDateFrom(v.dateFrom);
            if (typeof v.dateTo === 'string') setDateTo(v.dateTo);
            if (typeof v.paymentTrackFilter === 'string') setPaymentTrackFilter(v.paymentTrackFilter);
            if (typeof v.ledgerPaymentYear === 'string') setLedgerPaymentYear(v.ledgerPaymentYear);
            if (typeof v.ledgerPaymentMonth === 'string') setLedgerPaymentMonth(v.ledgerPaymentMonth);
        } catch { /* ignore */ }
    }, []);

    useEffect(() => { load(); }, []);

    const saveView = useCallback(() => {
        try {
            localStorage.setItem(STORAGE_KEY, JSON.stringify({
                search, franchiseLine, officeBranch, dateFrom, dateTo,
                paymentTrackFilter, ledgerPaymentYear, ledgerPaymentMonth,
            }));
            toast.success('View saved — filters restored next visit.');
        } catch { toast.error('Could not save view'); }
    }, [search, franchiseLine, officeBranch, dateFrom, dateTo, paymentTrackFilter, ledgerPaymentYear, ledgerPaymentMonth]);

    const resetView = useCallback(() => {
        localStorage.removeItem(STORAGE_KEY);
        setSearch(''); setFranchiseLine(''); setOfficeBranch('');
        setDateFrom(''); setDateTo('');
        setPaymentTrackFilter(''); setLedgerPaymentYear(''); setLedgerPaymentMonth('');
        toast.success('View reset to defaults');
    }, []);

    /* ── derived dropdown options — read from c.data ── */
    const serviceLines = useMemo(() => {
        const s = new Set();
        clients.forEach((c) => { if (c.data?.franchiseType) s.add(c.data.franchiseType); });
        return [...s].sort();
    }, [clients]);

    const branchOptions = useMemo(() => {
        const s = new Set();
        clients.forEach((c) => { if (c.data?.officeBranch) s.add(c.data.officeBranch); });
        return [...s].sort();
    }, [clients]);

    const ledgerPaymentYearOptions = useMemo(() => {
        const ys = new Set();
        ys.add(new Date().getFullYear());
        clients.forEach((c) => {
            (c.secondPayments || []).forEach((p) => {
                if (p.status !== 'approved') return;
                const d = new Date(p.paymentDate || p.createdAt);
                if (!isNaN(d)) ys.add(d.getFullYear());
            });
            if (c.data?.tokenDate) {
                const d = new Date(c.data.tokenDate);
                if (!isNaN(d)) ys.add(d.getFullYear());
            }
        });
        return [...ys].sort((a, b) => b - a);
    }, [clients]);

    /* ── filtered list ── */
    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        const from = dateFrom ? new Date(dateFrom) : null;
        const to = dateTo ? new Date(`${dateTo}T23:59:59`) : null;

        return clients.filter((c) => {
            const d = c.data || {};
            if (q) {
                const hay = [
                    c._id, d.name, d.phone, d.email,
                    d.franchiseCity, d.personalCity,
                    d.franchiseType, d.officeBranch, d.firmName,
                    d.bdmName, d.bheadName,
                    c.submittedBy?.name,
                ].filter(Boolean).join(' ').toLowerCase();
                if (!hay.includes(q)) return false;
            }
            if (from && new Date(c.createdAt) < from) return false;
            if (to && new Date(c.createdAt) > to) return false;
            if (franchiseLine && d.franchiseType !== franchiseLine) return false;
            if (officeBranch && d.officeBranch !== officeBranch) return false;

            /* payment track filter */
            if (paymentTrackFilter) {
                const m = getPaymentMetrics(c);
                if (m.finalPayable <= 0) return false;
                if (paymentTrackFilter === 'pending_verify' && m.pendingVerify === 0) return false;
                if (paymentTrackFilter === 'shortfall' && m.receivedPercent >= 100) return false;
                if (paymentTrackFilter === 'complete' && m.receivedPercent < 100) return false;
            }

            /* payment year / month filter */
            if (ledgerPaymentYear) {
                const y = Number(ledgerPaymentYear);
                const mIdx = ledgerPaymentMonth === '' ? null : Number(ledgerPaymentMonth) - 1;
                const allPmts = [
                    ...(c.secondPayments || [])
                        .filter((p) => p.status === 'approved')
                        .map((p) => new Date(p.paymentDate || p.createdAt)),
                    d.tokenDate ? new Date(d.tokenDate) : null,
                ].filter(Boolean);
                const inRange = allPmts.some((dt) => {
                    if (isNaN(dt)) return false;
                    if (dt.getFullYear() !== y) return false;
                    if (mIdx != null && dt.getMonth() !== mIdx) return false;
                    return true;
                });
                if (!inRange) return false;
            }
            return true;
        });
    }, [clients, search, dateFrom, dateTo, franchiseLine, officeBranch, paymentTrackFilter, ledgerPaymentYear, ledgerPaymentMonth]);

    /* ── financial aggregates ── */
    const financials = useMemo(() => aggregateFinancials(filtered), [filtered]);

    /* ── summary stats ── */
    const stats = useMemo(() => {
        const total = clients.length;
        const complete = clients.filter((c) => getPaymentMetrics(c).receivedPercent >= 100).length;
        const partial = clients.filter((c) => {
            const m = getPaymentMetrics(c);
            return m.finalPayable > 0 && m.receivedPercent < 100;
        }).length;
        const pending = clients.filter((c) => getPaymentMetrics(c).pendingVerify > 0).length;
        return { total, complete, partial, pending };
    }, [clients]);

    /* ── Excel export ── */
    const exportExcel = async () => {
        try {
            const ExcelJS = (await import('exceljs')).default;
            const { saveAs } = await import('file-saver');
            const wb = new ExcelJS.Workbook();
            const ws = wb.addWorksheet('Payment Ledger');

            const baseCols = [
                { header: 'S No.', key: 'sno', width: 6 },
                { header: 'Client ID', key: 'clientId', width: 14 },
                { header: 'Client', key: 'name', width: 24 },
                { header: 'Franchise Model', key: 'franchise', width: 22 },
                { header: 'Territory', key: 'territory', width: 18 },
                { header: 'State', key: 'state', width: 14 },
                { header: 'BDM', key: 'bdm', width: 14 },
                { header: 'Head', key: 'bhead', width: 14 },
                { header: 'Deal', key: 'deal', width: 14 },
                { header: 'GST', key: 'gst', width: 14 },
                { header: 'TDS', key: 'tds', width: 14 },
                { header: 'Payable', key: 'payable', width: 14 },
                { header: 'Collected', key: 'collected', width: 14 },
                { header: 'Collected %', key: 'colPct', width: 12 },
                { header: 'Balance', key: 'balance', width: 14 },
                { header: 'Token', key: 'token', width: 14 },
                { header: 'Token Date', key: 'tokenDate', width: 14 },
                { header: 'Instalments', key: 'inst', width: 14 },
                { header: 'Pending', key: 'pending', width: 14 },
                ...MONTHS.map((m) => ({ header: m, key: m, width: 22 })),
            ];
            ws.columns = baseCols;

            const hdr = ws.getRow(1);
            hdr.font = { bold: true, color: { argb: 'FFFFFFFF' } };
            hdr.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF0F172A' } };
            hdr.alignment = { vertical: 'middle', horizontal: 'center' };
            hdr.height = 24;

            filtered.forEach((c, i) => {
                const d = c.data || {};
                const m = getPaymentMetrics(c);
                const mth = groupByMonth(c);
                const row = {
                    sno: i + 1,
                    clientId: ageonId(c),
                    name: d.name || '—',
                    franchise: getFranchiseLabel(d.franchiseType),
                    territory: d.franchiseCity || d.personalCity || '—',
                    state: d.franchiseState || d.personalState || '—',
                    bdm: d.bdmName || '—',
                    bhead: d.bheadName || '—',
                    deal: m.deal,
                    gst: m.gst,
                    tds: m.tds,
                    payable: m.finalPayable,
                    collected: m.totalReceived,
                    colPct: m.receivedPercent / 100,
                    balance: m.balance,
                    token: m.tokenNet,
                    tokenDate: d.tokenDate ? new Date(d.tokenDate).toLocaleDateString('en-IN') : '—',
                    inst: (c.secondPayments || []).filter((p) => p.status === 'approved').length,
                    pending: m.balance,
                };
                MONTHS.forEach((mn) => {
                    row[mn] = mth[mn].length > 0
                        ? mth[mn].map((p) =>
                            `${p.isToken ? 'Token: ' : ''}${p.date.toLocaleDateString('en-IN')} ${inr(p.amount)}`
                        ).join('\n')
                        : '—';
                });
                const exRow = ws.addRow(row);
                ['I', 'J', 'K', 'L', 'M', 'O', 'P', 'S'].forEach((col) => {
                    exRow.getCell(col).numFmt = '₹#,##,##0';
                });
                exRow.getCell('N').numFmt = '0.00%';
                if (i % 2 === 0) {
                    exRow.eachCell({ includeEmpty: true }, (cell) => {
                        if (!cell.fill?.fgColor) {
                            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF8FAFC' } };
                        }
                    });
                }
            });

            ws.views = [{ state: 'frozen', xSplit: 3, ySplit: 1 }];
            const buf = await wb.xlsx.writeBuffer();
            saveAs(new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }),
                `ageon-payment-ledger-${new Date().toISOString().slice(0, 10)}.xlsx`);
            toast.success('Excel downloaded');
        } catch (e) {
            console.error(e);
            toast.error('Export failed');
        }
    };

    const exportCsv = () => {
        const headers = ['S No.', 'Client ID', 'Client', 'Franchise', 'Territory', 'State',
            'BDM', 'Head', 'Deal', 'GST', 'TDS', 'Payable', 'Collected', 'Collected %', 'Balance',
            'Token', 'Instalments', ...MONTHS];
        const rows = filtered.map((c, i) => {
            const d = c.data || {};
            const m = getPaymentMetrics(c);
            const mth = groupByMonth(c);
            return [
                i + 1,
                ageonId(c),
                d.name || '',
                getFranchiseLabel(d.franchiseType),
                d.franchiseCity || d.personalCity || '',
                d.franchiseState || d.personalState || '',
                d.bdmName || '',
                d.bheadName || '',
                m.deal, m.gst, m.tds, m.finalPayable, m.totalReceived,
                `${m.receivedPercent.toFixed(1)}%`, m.balance, m.tokenNet,
                (c.secondPayments || []).filter((p) => p.status === 'approved').length,
                ...MONTHS.map((mn) => mth[mn].map((p) => inr(p.amount)).join(' + ') || '—'),
            ].map(csvEscape).join(',');
        });
        const blob = new Blob([['\ufeff', headers.map(csvEscape).join(','), ...rows].join('\n')],
            { type: 'text/csv;charset=utf-8;' });
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = `ageon-payment-ledger-${new Date().toISOString().slice(0, 10)}.csv`;
        a.click();
        URL.revokeObjectURL(a.href);
    };

    const card = 'rounded-xl border border-slate-200/90 bg-white shadow-sm';

    /* ══════════════════════════════ RENDER ══════════════════════════════ */
    return (
        <div className="space-y-4 -mx-1 sm:-mx-2">

            {/* ── Header ── */}
            <div className={`${card} p-5 sm:p-6`}>
                <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                    <div>
                        <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900 font-heading">
                            Payment ledger
                        </h1>
                        <p className="mt-1 text-sm text-slate-600">
                            <span className="font-semibold text-slate-900 tabular-nums">{filtered.length}</span>
                            {' '}client{filtered.length === 1 ? '' : 's'} shown
                            <span className="text-slate-400"> · </span>
                            <span className="tabular-nums">{clients.length}</span> loaded from server
                        </p>
                        <p className="mt-2 text-xs">
                            <Link to="/admin/clients" className="font-semibold text-teal-700 hover:text-teal-900">
                                ← All clients
                            </Link>
                        </p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                        <button type="button" onClick={exportCsv}
                            className="inline-flex items-center gap-2 rounded-lg bg-sky-600 px-3.5 py-2 text-xs font-bold text-white shadow-sm hover:bg-sky-700 transition">
                            <DLIcon className="h-4 w-4" /> CSV
                        </button>
                        <button type="button" onClick={exportExcel}
                            className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-3.5 py-2 text-xs font-bold text-white shadow-sm hover:bg-emerald-700 transition">
                            <FileSpreadsheet className="h-4 w-4" /> Excel
                        </button>
                        <button type="button" onClick={saveView}
                            className="inline-flex items-center gap-2 rounded-lg bg-teal-600 px-3.5 py-2 text-xs font-bold text-white shadow-sm hover:bg-teal-700 transition">
                            <Save className="h-4 w-4" /> Save view
                        </button>
                        <button type="button" onClick={resetView}
                            className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-slate-50 px-3.5 py-2 text-xs font-bold text-slate-700 hover:bg-slate-100 transition">
                            <RotateCcw className="h-4 w-4" /> Reset
                        </button>
                        <button type="button" onClick={() => load(true)}
                            className="inline-flex items-center gap-2 rounded-lg border border-slate-200 px-3.5 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50 transition">
                            <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} /> Reload
                        </button>
                    </div>
                </div>
            </div>

            {/* ── Portfolio totals ── */}
            <div className={`${card} p-5 sm:p-6 space-y-5`}>
                <div className="flex flex-wrap items-center gap-3">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl
                                    bg-gradient-to-br from-slate-800 to-teal-800 text-white shadow">
                        <Wallet className="h-5 w-5" />
                    </div>
                    <div>
                        <h2 className="text-sm font-extrabold text-slate-900 tracking-tight">Portfolio totals</h2>
                        <p className="text-xs text-slate-500">Financial summary for the filtered list.</p>
                    </div>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                    <AccentFinCard label="Total deal value" value={inr(financials.deal)} labelClass="text-[#1e3a5f]" />
                    <AccentFinCard label="Total payable" value={inr(financials.payable)} labelClass="text-sky-700" />
                    <AccentFinCard label="Total collected" value={inr(financials.collected)} labelClass="text-emerald-800" />
                    <AccentFinCard label="Balance due" value={inr(financials.balanceDue)} labelClass="text-amber-800" />
                    <AccentFinCard label="Total GST" value={inr(financials.gst)} labelClass="text-teal-800" />
                    <AccentFinCard label="Total TDS" value={inr(financials.tds)} labelClass="text-violet-800" />
                    <AccentFinCard label="Token received" value={inr(financials.token)} labelClass="text-blue-800" />
                    <AccentFinCard label="Instalments" value={inr(financials.inst)} labelClass="text-cyan-900" />
                    <AccentFinCard label="Total refunds" value={inr(financials.ref)} labelClass="text-purple-800" />
                </div>

                {/* Collection rate bar */}
                <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-inner">
                    <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-sky-800">Collection rate</div>
                    <div className="mt-2 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                        <div className="font-heading text-3xl font-extrabold tabular-nums text-slate-900">
                            {pct(financials.colRate, 1)}
                        </div>
                        <div className="flex-1 max-w-md">
                            <div className="h-2.5 w-full overflow-hidden rounded-full bg-slate-200">
                                <div className="h-full rounded-full bg-gradient-to-r from-sky-500 to-blue-600 transition-[width] duration-500"
                                    style={{ width: `${Math.min(100, financials.colRate)}%` }} />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Stat cards */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                    <StatCard icon={Activity} label="Total clients" value={stats.total} sub={`${inr(financials.deal)} volume`} />
                    <StatCard icon={CheckCircle2} label="Fully paid" value={stats.complete} sub="100% collected" />
                    <StatCard icon={Clock} label="Partial payment" value={stats.partial} sub="Below 100%" />
                    <StatCard icon={BarChart3} label="Pending verification" value={stats.pending} sub="Awaiting review" />
                </div>
            </div>

            {/* ── Filter panel ── */}
            <div className={`${card} overflow-hidden ring-1 ring-teal-500/10`}>
                <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100
                                bg-gradient-to-r from-slate-50 via-white to-teal-50/40 px-4 py-3 sm:px-5">
                    <div className="flex items-center gap-2 min-w-0">
                        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-teal-600 text-white shadow-sm">
                            <Wallet className="h-4 w-4" />
                        </div>
                        <div>
                            <h2 className="text-sm font-extrabold text-slate-900 tracking-tight">Payment register</h2>
                            <p className="text-[11px] text-slate-500">Deal, GST, TDS and collection — Ageon layout.</p>
                        </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                        <button type="button" onClick={() => setShowLedgerFilters((v) => !v)}
                            className={`inline-flex items-center gap-1.5 rounded-lg border px-3 py-2 text-xs font-bold transition
                                ${showLedgerFilters
                                    ? 'border-teal-500 bg-teal-50 text-teal-900'
                                    : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'}`}>
                            <Filter className="h-3.5 w-3.5" /> Filters
                        </button>
                        <span className="text-xs font-semibold text-slate-500 tabular-nums whitespace-nowrap">
                            {filtered.length} of {clients.length} shown
                        </span>
                    </div>
                </div>

                <div className="p-4 sm:p-5 space-y-4">
                    <div className="flex flex-col gap-3 lg:flex-row lg:items-end">
                        <div className="flex-1 min-w-0">
                            <label className="block text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-1">Search</label>
                            <div className="relative">
                                <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                                <input
                                    className="w-full rounded-xl border border-slate-200 bg-white py-2.5 pl-10 pr-3 text-sm
                                               shadow-inner focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-500/15"
                                    placeholder="Client name, ID, territory, BDM, head…"
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                />
                            </div>
                        </div>

                        <div className="flex flex-wrap gap-2">
                            <div>
                                <label className="block text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-1">Collection</label>
                                <select className="rounded-xl border border-slate-200 bg-white py-2 px-3 text-sm min-w-[11rem]"
                                    value={paymentTrackFilter} onChange={(e) => setPaymentTrackFilter(e.target.value)}>
                                    <option value="">All</option>
                                    <option value="pending_verify">Pending verify</option>
                                    <option value="shortfall">Below 100%</option>
                                    <option value="complete">Fully collected</option>
                                </select>
                            </div>
                            <div>
                                <label className="block text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-1">Payment year</label>
                                <select className="rounded-xl border border-slate-200 bg-white py-2 px-3 text-sm min-w-[8.5rem]"
                                    value={ledgerPaymentYear}
                                    onChange={(e) => {
                                        setLedgerPaymentYear(e.target.value);
                                        if (!e.target.value) setLedgerPaymentMonth('');
                                    }}>
                                    <option value="">All years</option>
                                    {ledgerPaymentYearOptions.map((y) => (
                                        <option key={y} value={String(y)}>{y}</option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="block text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-1">Payment month</label>
                                <select
                                    className="rounded-xl border border-slate-200 bg-white py-2 px-3 text-sm min-w-[9.5rem]
                                               disabled:opacity-50 disabled:cursor-not-allowed"
                                    value={ledgerPaymentMonth}
                                    disabled={!ledgerPaymentYear}
                                    onChange={(e) => setLedgerPaymentMonth(e.target.value)}>
                                    <option value="">All months</option>
                                    {Array.from({ length: 12 }, (_, i) => (
                                        <option key={i + 1} value={String(i + 1)}>
                                            {new Date(2000, i, 1).toLocaleString('en-IN', { month: 'long' })}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    </div>

                    {ledgerPaymentYear && (
                        <p className="text-[11px] text-slate-500">
                            Showing clients with at least one approved payment in{' '}
                            {ledgerPaymentMonth
                                ? `${new Date(2000, Number(ledgerPaymentMonth) - 1, 1)
                                    .toLocaleString('en-IN', { month: 'long' })} ${ledgerPaymentYear}`
                                : `calendar year ${ledgerPaymentYear}`}.
                        </p>
                    )}

                    {showLedgerFilters && (
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3
                                        rounded-xl border border-dashed border-slate-200 bg-slate-50/60 p-4">
                            <div>
                                <label className="block text-[11px] font-bold uppercase text-slate-500 mb-1">Franchise type</label>
                                <select className="w-full rounded-lg border border-slate-200 bg-white py-2 px-2 text-sm"
                                    value={franchiseLine} onChange={(e) => setFranchiseLine(e.target.value)}>
                                    <option value="">All</option>
                                    {serviceLines.map((x) => <option key={x} value={x}>{getFranchiseLabel(x)}</option>)}
                                </select>
                            </div>
                            <div>
                                <label className="block text-[11px] font-bold uppercase text-slate-500 mb-1">Branch</label>
                                <select className="w-full rounded-lg border border-slate-200 bg-white py-2 px-2 text-sm"
                                    value={officeBranch} onChange={(e) => setOfficeBranch(e.target.value)}>
                                    <option value="">All</option>
                                    {branchOptions.map((x) => <option key={x} value={x}>{x}</option>)}
                                </select>
                            </div>
                            <div>
                                <label className="block text-[11px] font-bold uppercase text-slate-500 mb-1">From</label>
                                <input type="date" className="w-full rounded-lg border border-slate-200 bg-white py-2 px-2 text-sm"
                                    value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
                            </div>
                            <div>
                                <label className="block text-[11px] font-bold uppercase text-slate-500 mb-1">To</label>
                                <input type="date" className="w-full rounded-lg border border-slate-200 bg-white py-2 px-2 text-sm"
                                    value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* ── Ledger table ── */}
            <div className={`${card} overflow-hidden border-slate-200/90`}>
                <div className="overflow-x-auto overscroll-x-contain [scrollbar-gutter:stable]">
                    <table className="w-full text-sm border-collapse" style={{ minWidth: `${1400 + MONTHS.length * 120}px` }}>
                        <thead>
                            <tr className="bg-gradient-to-r from-slate-900 via-slate-800 to-teal-900 text-white">
                                <th className="sticky left-0 z-30 w-10 min-w-[2.5rem] border-r border-white/10 bg-slate-900 px-2 py-3 text-[10px] font-bold uppercase tracking-wider">
                                    #
                                </th>
                                <th className="sticky left-[2.5rem] z-30 w-[9rem] min-w-[9rem] border-r border-white/10 bg-slate-900 px-2 py-3 text-[10px] font-bold uppercase tracking-wider">
                                    Client ID
                                </th>
                                <th className="sticky left-[11.5rem] z-30 min-w-[12rem] max-w-[14rem] border-r border-white/15 bg-slate-900 px-3 py-3 text-[10px] font-bold uppercase tracking-wider shadow-[6px_0_16px_-6px_rgba(0,0,0,0.35)]">
                                    Client
                                </th>
                                <th className="whitespace-nowrap px-3 py-3 text-[10px] font-bold uppercase tracking-wider text-slate-300">Model</th>
                                <th className="whitespace-nowrap px-3 py-3 text-[10px] font-bold uppercase tracking-wider text-slate-300">Territory</th>
                                <th className="whitespace-nowrap px-3 py-3 text-[10px] font-bold uppercase tracking-wider text-slate-300">State</th>
                                <th className="whitespace-nowrap px-3 py-3 text-[10px] font-bold uppercase tracking-wider text-slate-300">BDM</th>
                                <th className="whitespace-nowrap px-3 py-3 text-[10px] font-bold uppercase tracking-wider text-slate-300">Head</th>
                                <th className="whitespace-nowrap px-3 py-3 text-right text-[10px] font-bold uppercase tracking-wider text-slate-200">Deal</th>
                                <th className="whitespace-nowrap px-3 py-3 text-right text-[10px] font-bold uppercase tracking-wider text-rose-200">GST</th>
                                <th className="whitespace-nowrap px-3 py-3 text-right text-[10px] font-bold uppercase tracking-wider text-rose-200">TDS</th>
                                <th className="whitespace-nowrap px-3 py-3 text-right text-[10px] font-bold uppercase tracking-wider text-slate-200">Payable</th>
                                <th className="whitespace-nowrap px-3 py-3 text-right text-[10px] font-bold uppercase tracking-wider text-slate-200">%</th>
                                <th className="whitespace-nowrap px-3 py-3 text-right text-[10px] font-bold uppercase tracking-wider text-slate-200">Balance</th>
                                <th className="whitespace-nowrap px-3 py-3 text-[10px] font-bold uppercase tracking-wider text-slate-300 min-w-[9rem]">Tracker</th>
                                {MONTHS.map((mn) => (
                                    <th key={mn} className="whitespace-nowrap border-l border-white/10 px-3 py-3 text-center text-[10px] font-bold uppercase tracking-wider text-slate-300 min-w-[9rem]">
                                        {mn}
                                    </th>
                                ))}
                                <th className="sticky right-0 z-30 min-w-[5.5rem] border-l border-white/10 bg-slate-900 px-3 py-3 text-right text-[10px] font-bold uppercase tracking-wider text-slate-300 shadow-[-8px_0_16px_-6px_rgba(0,0,0,0.4)]">
                                    {' '}
                                </th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100 bg-white">
                            {filtered.map((c, i) => {
                                const d = c.data || {};
                                const m = getPaymentMetrics(c);
                                const mth = groupByMonth(c);
                                const inactive = m.finalPayable <= 0;
                                return (
                                    <tr key={c._id}
                                        className="group odd:bg-white even:bg-slate-50/90 hover:bg-teal-50/40 transition-colors cursor-pointer"
                                        onClick={() => navigate(`/admin/clients/${c._id}`)}>
                                        {/* Sticky cells */}
                                        <td className="sticky left-0 z-20 border-r border-slate-200/80 bg-white px-2 py-2.5 text-xs font-mono text-slate-500 tabular-nums group-even:bg-slate-50/95">
                                            {i + 1}
                                        </td>
                                        <td className="sticky left-[2.5rem] z-20 w-[9rem] min-w-[9rem] border-r border-slate-200/80 bg-white px-2 py-2.5 font-mono text-[11px] font-semibold text-teal-800 tabular-nums group-even:bg-slate-50/95">
                                            {ageonId(c)}
                                        </td>
                                        <td className="sticky left-[11.5rem] z-20 min-w-[12rem] max-w-[14rem] border-r border-slate-200/90 bg-white px-3 py-2.5 font-bold text-slate-900 truncate shadow-[6px_0_14px_-8px_rgba(15,23,42,0.12)] group-even:bg-slate-50/95"
                                            title={d.name || ''}>
                                            {d.name || '—'}
                                        </td>
                                        {/* Scrollable cells */}
                                        <td className="px-3 py-2.5 text-xs text-slate-700 max-w-[9rem] truncate">
                                            {getFranchiseLabel(d.franchiseType)}
                                        </td>
                                        <td className="px-3 py-2.5 text-xs text-slate-600 max-w-[8rem] truncate">
                                            {d.franchiseCity || d.personalCity || '—'}
                                        </td>
                                        <td className="px-3 py-2.5 text-xs text-slate-600 max-w-[7rem] truncate">
                                            {d.franchiseState || d.personalState || '—'}
                                        </td>
                                        <td className="px-3 py-2.5 text-xs text-slate-600 max-w-[7rem] truncate">
                                            {d.bdmName || '—'}
                                        </td>
                                        <td className="px-3 py-2.5 text-xs text-slate-600 max-w-[7rem] truncate">
                                            {d.bheadName || '—'}
                                        </td>
                                        <td className="px-3 py-2.5 text-right text-sm font-bold tabular-nums text-slate-900 whitespace-nowrap">
                                            {inactive ? '—' : inr(m.deal)}
                                        </td>
                                        <td className="px-3 py-2.5 text-right text-sm font-semibold tabular-nums text-rose-700 whitespace-nowrap bg-rose-50/50">
                                            {inactive ? '—' : inr(m.gst)}
                                        </td>
                                        <td className="px-3 py-2.5 text-right text-sm font-semibold tabular-nums text-rose-700 whitespace-nowrap bg-rose-50/50">
                                            {inactive ? '—' : inr(m.tds)}
                                        </td>
                                        <td className="px-3 py-2.5 text-right text-sm font-bold tabular-nums text-slate-800 whitespace-nowrap">
                                            {inactive ? '—' : inr(m.finalPayable)}
                                        </td>
                                        <td className="px-3 py-2.5 text-right text-sm font-bold tabular-nums text-teal-800 whitespace-nowrap">
                                            {inactive ? '—' : pct(m.receivedPercent)}
                                        </td>
                                        <td className="px-3 py-2.5 text-right text-sm font-semibold tabular-nums text-slate-800 whitespace-nowrap">
                                            {inactive ? '—' : inr(m.balance)}
                                        </td>
                                        <td className="px-3 py-2.5 align-middle min-w-[9rem]">
                                            <PaymentTrackerCell client={c} />
                                        </td>
                                        {/* Monthly cells */}
                                        {MONTHS.map((mn) => (
                                            <td key={mn} className="border-l border-slate-100 px-2 py-2.5 text-center align-top">
                                                <div className="min-h-[3rem]">
                                                    {mth[mn].length === 0
                                                        ? <span className="text-slate-300 text-xs">—</span>
                                                        : (
                                                            <div className="space-y-1">
                                                                {mth[mn].map((p, idx) => (
                                                                    <div key={idx} className="border-b border-slate-100 pb-1 last:border-0">
                                                                        <div className="text-[10px] text-slate-500">
                                                                            {p.date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}
                                                                        </div>
                                                                        <div className={`text-xs font-semibold ${p.isToken ? 'text-blue-700' : 'text-emerald-700'}`}>
                                                                            {p.isToken && <span className="text-[9px] mr-0.5 font-bold">TKN</span>}
                                                                            {inr(p.amount)}
                                                                        </div>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        )}
                                                </div>
                                            </td>
                                        ))}
                                        {/* Sticky open button */}
                                        <td className="sticky right-0 z-20 border-l border-slate-200/80 bg-white px-2 py-2.5 text-right shadow-[-8px_0_14px_-8px_rgba(15,23,42,0.1)] group-even:bg-slate-50/95"
                                            onClick={(e) => e.stopPropagation()}>
                                            <Link to={`/admin/clients/${c._id}`}
                                                className="inline-flex items-center justify-center rounded-lg bg-teal-600 px-2.5 py-1.5 text-[10px] font-bold text-white hover:bg-teal-700">
                                                Open
                                            </Link>
                                        </td>
                                    </tr>
                                );
                            })}
                            {filtered.length === 0 && (
                                <tr>
                                    <td colSpan={17 + MONTHS.length}
                                        className="px-6 py-16 text-center text-slate-500 text-sm">
                                        {loading ? 'Loading…' : clients.length === 0 ? 'No clients yet.' : 'No rows match these filters.'}
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
                <div className="flex flex-wrap gap-x-6 gap-y-1 border-t border-slate-100 bg-slate-50/80 px-4 py-2.5 text-[11px] text-slate-500">
                    <span>Scroll horizontally for financial columns and monthly breakdown.</span>
                    <span className="font-medium text-slate-600"># · Client ID · Client</span>
                    <span>stay pinned while you scan across.</span>
                    <span className="font-medium text-blue-700">TKN</span>
                    <span>= token payment · green = instalment.</span>
                </div>
            </div>
        </div>
    );
}